/** Site cycle year is the America/New_York calendar year unless temporarily overridden. */

export const CYCLE_YEAR_OVERRIDE_ENABLED_KEY = 'CYCLE_YEAR_OVERRIDE_ENABLED';
export const CYCLE_YEAR_OVERRIDE_KEY = 'CYCLE_YEAR_OVERRIDE';

const EST_TIME_ZONE = 'America/New_York';

/** Current calendar year in Eastern Time (handles EST/EDT). */
export function getEstCalendarYear(date: Date = new Date()): number {
	const parts = new Intl.DateTimeFormat('en-US', {
		timeZone: EST_TIME_ZONE,
		year: 'numeric',
	}).formatToParts(date);
	const yearPart = parts.find((part) => part.type === 'year')?.value;
	const year = yearPart ? Number(yearPart) : NaN;
	return Number.isFinite(year) ? year : date.getFullYear();
}

export function parseCycleYearValue(value: unknown): number | null {
	if (typeof value === 'number' && Number.isFinite(value)) {
		return Math.trunc(value);
	}
	if (typeof value === 'string' && value.trim()) {
		const match = value.trim().match(/^(\d{4})/);
		if (!match) return null;
		const year = Number(match[1]);
		if (Number.isFinite(year) && year >= 1900 && year <= 2100) return year;
	}
	return null;
}

export function isCycleYearOverrideEnabled(
	config: Record<string, unknown> | null | undefined
): boolean {
	return Boolean(config?.[CYCLE_YEAR_OVERRIDE_ENABLED_KEY]);
}

/**
 * Live site cycle year: EST calendar year, or CYCLE_YEAR_OVERRIDE when override is enabled.
 * Falls back to a numeric CYCLE_YEAR only while override is on (legacy / partial saves).
 */
export function resolveEffectiveCycleYear(
	config: Record<string, unknown> | null | undefined,
	now: Date = new Date()
): number {
	if (isCycleYearOverrideEnabled(config)) {
		const override =
			parseCycleYearValue(config?.[CYCLE_YEAR_OVERRIDE_KEY]) ??
			parseCycleYearValue(config?.CYCLE_YEAR);
		if (override != null) return override;
	}
	return getEstCalendarYear(now);
}

/** Fields that belong to the dedicated Cycle Year control, not the generic settings editor. */
export const CYCLE_YEAR_SETTING_KEYS = new Set([
	'CYCLE_YEAR',
	CYCLE_YEAR_OVERRIDE_ENABLED_KEY,
	CYCLE_YEAR_OVERRIDE_KEY,
]);
